import React, { useState, useRef, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Search, ArrowUp } from "lucide-react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useAuditUsers,
  useValidationBreakList,
} from "@features/mx/validations/hooks";
import { useUserUtilityServices } from "@features/mx/settings/hooks";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { FormField } from "@shared/services/FormServices";
import CycleFilters from "../ActiveCycles/CycleFilters";
import ActiveCycleCard from "./ActiveCycleCard";
import { useDebounce } from "@shared/hooks/useDebounce";

function ActiveCycles({
  onCycleCountChange,
}: {
  onCycleCountChange?: (data: { total: number; shown: number }) => void;
}) {
  const remoteUtilityId = useRemoteUtilityId();
  const [limit, setLimit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevScrollTop = useRef<number>(0);

  const page = 1;
  const search = useDebounce(searchTerm, 300)
  const { data: activeCycles, isFetching, refetch: refetchCycles } = useValidationBreakList({
    remoteUtilityId,
    page,
    limit,
    filters,
    search,
    isCompleted: false,
  });

  const { data: utilityServices } = useUserUtilityServices(remoteUtilityId);
  const { data: readTypes } = useChoices({ choice: "meter_type" });
  const { data: validatorsList } = useAuditUsers(
    remoteUtilityId,
    0, // validator = 0
    showFilters
  );
  const { data: supervisorList } = useAuditUsers(
    remoteUtilityId,
    2, // supervisor = 2
    showFilters
  );

  const validatorDropdownOptions = mapToLabelValue(
    validatorsList,
    "name",
    "id"
  );
  const supervisorDropdownOptions = mapToLabelValue(
    supervisorList,
    "name",
    "id"
  );

  const activeUtilityServices = utilityServices?.filter(
    (service) => service.isActive
  );
  const utilityServicesList = mapToLabelValue(
    activeUtilityServices,
    "name",
    "name"
  );
  const readTypeList = mapToLabelValue(readTypes, "value", "key");

  // 🔹 Track scroll position for Back-to-Top button
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      setShowBackToTop(scrollContainer.scrollTop > 200);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // ⬇️ Remember scroll position before refetch
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    prevScrollTop.current = scrollContainer.scrollTop;
  }, [limit]);

  // ⬆️ Restore scroll after data updates
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !activeCycles?.results?.length) return;
    scrollContainer.scrollTop = prevScrollTop.current;
  }, [activeCycles?.results]);

  const handleBackToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (activeCycles) {
      onCycleCountChange?.({
        total: activeCycles?.count || 0,
        shown: activeCycles?.results?.length || 0,
      });
    }
  }, [activeCycles]);
  const handleShowMore = () => {
    setLimit((prev) => prev + 10);
  };

  const filterFields: FormField[] = [
    { label: "Start Date", name: "start_date", type: "datepicker" },
    { label: "End Date", name: "end_date", type: "datepicker" },
    {
      label: "Read Type",
      name: "meter_type",
      type: "select",
      options: readTypeList,
    },
    {
      label: "Utility",
      name: "utility_service",
      type: "select",
      options: utilityServicesList,
    },
    {
      label: "Validator",
      name: "validator_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: validatorDropdownOptions,
    },
    {
      label: "Supervisor",
      name: "supervisor_id",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      options: supervisorDropdownOptions,
    },
  ];

  const handleSubmit = (values: any) => {
    setFilters(values);
    setLimit(10);
  };

  return (
    <div className="relative">
      {/* 🔍 Search & Filters */}
      <div className="flex items-center justify-between gap-4 pr-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search cycles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>

      {/* 🧭 Filters Section */}
      {showFilters && (
        <CycleFilters filterFields={filterFields} onSubmit={handleSubmit} />
      )}

      {/* 🔹 Active Cycles Grid */}
      {!activeCycles?.results?.length ? (
        <div className="text-center text-muted-foreground mt-4">
          No Active Cycles
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            className="grid grid-cols-3 gap-4 h-[70vh] overflow-y-scroll mt-4 relative"
          >
            {activeCycles.results.map((cycle: any, index: number) => (
              <ActiveCycleCard cycle={cycle} key={index} refetchCycle={refetchCycles}  />
            ))}

            {/* 🔹 Show More */}
            {activeCycles.results.length >= limit && (
              <Button
                onClick={handleShowMore}
                disabled={isFetching}
                className="w-full mt-4 col-span-3"
              >
                Show More
              </Button>
            )}
          </div>

          {/* ⬆️ Back to Top */}
          {showBackToTop && (
            <Button
              size="icon"
              onClick={handleBackToTop}
              className="absolute bottom-11 right-8 rounded-full shadow-md bg-primary text-white hover:bg-primary/90"
            >
              <ArrowUp className="h-5 w-5" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default ActiveCycles;
